import {
  ComponentPropsWithoutRef,
  FC,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { EXTERNAL_URL } from "@hl/shared-features/lib/config";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks/useFeatureFlags";
import { getNativeCurrencyIcon } from "@hl/shared-features/lib/utils/currency";
import {
  Anchor,
  Grid,
  Group,
  MantineNumberSize,
  Select,
  SelectProps,
  Stack,
  Text,
} from "@mantine/core";

import { NetworkType, NftContractStandard } from "apollo/graphql.generated";
import { useCreateCollectionFormContext } from "~/features/collections/forms/CreateCollectionMultistepForm/context";
import { GetSupportedChainsQuery } from "~/features/tokens/queries/configuration.graphql.generated";

import { BlockchainMainnets } from "../../../config";
import { SelectBox } from "../../sale/SelectBox";

interface ItemProps extends ComponentPropsWithoutRef<"div"> {
  image: string;
  label: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ label, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap>
        {getNativeCurrencyIcon(label, 20)}
        <div>
          <Text size="sm">{label}</Text>
        </div>
      </Group>
    </div>
  )
);
export type Props = Partial<SelectProps> & {
  supportedChains: GetSupportedChainsQuery["getSupportedChains"];
  value?: string;
  spacing: MantineNumberSize;
};

type MainnetNetworkTypes =
  | NetworkType.ETHEREUM
  | NetworkType.POLYGON
  | NetworkType.BASE
  | NetworkType.ARBITRUM
  | NetworkType.OPTIMISM
  | NetworkType.ZORA
  | NetworkType.FORMA
  | NetworkType.MANTLE
  | NetworkType.SCROLL
  | NetworkType.ZK_SYNC_ERA
  | NetworkType.SHAPE;
const AllowedTestnets = [NetworkType.SEPOLIA, NetworkType.BASE_SEPOLIA];

const BlockchainSelect: FC<Props> = ({
  supportedChains = [],
  onChange,
  value = undefined,
  searchable = true,
  spacing = 40,
  placeholder = "Pick one",
  ...props
}: Props) => {
  const getNetworkFromChainId = (chainId?: string): NetworkType => {
    if (!chainId) return NetworkType.SEPOLIA;
    const chain = supportedChains.find(
      (chain) => chain.chainId.toString() === chainId
    );
    if (!chain) return NetworkType.SEPOLIA;
    if (
      BlockchainMainnets.includes(chain.network) ||
      AllowedTestnets.includes(chain.network)
    )
      return chain.network;
    return NetworkType.SEPOLIA; // handles goerli drafts as a fallback
  };

  const initialNetwork = getNetworkFromChainId(value);
  const [mainnetSelected, setMainnetSelected] = useState<boolean>(
    BlockchainMainnets.includes(initialNetwork)
  );
  const [l2TestnetSelected, setL2TestnetSelected] = useState<boolean>(
    initialNetwork === NetworkType.BASE_SEPOLIA
  );
  const mainnetNetworks = useMemo(() => {
    return supportedChains.filter((supportedChain) => {
      return BlockchainMainnets.includes(supportedChain.network);
    });
  }, [supportedChains]);
  console.log(mainnetNetworks);
  console.log(BlockchainMainnets);
  console.log(supportedChains);

  const [mainnetNetwork, setMainnetNetwork] = useState<MainnetNetworkTypes>(
    mainnetSelected
      ? (initialNetwork as MainnetNetworkTypes)
      : (mainnetNetworks.find((supportedChain) => {
          return supportedChain.network === NetworkType.BASE;
        })?.network as MainnetNetworkTypes) ??
          mainnetNetworks[0] ??
          NetworkType.ETHEREUM
  );

  const enableFormaCollections = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_FORMA_COLLECTIONS
  );
  const enableShapeCollectionCreation = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_SHAPE_COLLECTION_CREATION
  );
  const filterChains = (mainnet: NetworkType) => {
    if (mainnet === NetworkType.FORMA && !enableFormaCollections) {
      return false;
    } else if (
      mainnet === NetworkType.SHAPE &&
      !enableShapeCollectionCreation
    ) {
      return false;
    }
    return true;
  };

  const mainnetOptions = BlockchainMainnets.filter(filterChains)
    .filter((mainnet) => {
      return !!supportedChains.find((supportedChain) => {
        return supportedChain.network === mainnet;
      });
    })
    .map((mainnet) => {
      const chain = supportedChains.find((supportedChain) => {
        return supportedChain.network === mainnet;
      });
      const label = chain?.name ?? mainnet.valueOf();
      return {
        label:
          chain && chain.network === NetworkType.ZK_SYNC_ERA
            ? "ZKsync Era"
            : label[0] + label.toLowerCase().slice(1),
        value: mainnet,
      };
    })
    .sort((a, b) => {
      return a.label.localeCompare(b.label);
    });

  // TODO: do we need a custom hook / logic for this, and if so, it's going to get intense
  // cases to handle:
  //    * user doesn't accept switching chains
  //    * metamask / provider fails to switch chains
  //    * on form, components inheriting this -> what to do while awaiting the switch?
  //    * what to display on selector when user doesn't switch
  //
  // for now: putting a chain check at form submission time

  const handleNetworkChange = useCallback(
    (network: NetworkType) => {
      if (BlockchainMainnets.includes(network)) {
        setMainnetSelected(true);
        setMainnetNetwork(network as MainnetNetworkTypes);
      } else {
        setMainnetSelected(false);
        setL2TestnetSelected(network === NetworkType.BASE_SEPOLIA);
      }
      const chain = supportedChains.find(
        (supportedChain) => supportedChain.network === network
      );
      if (chain && onChange) {
        onChange(chain.chainId.toString());
      }
    },
    [onChange]
  );

  useEffect(() => {
    handleNetworkChange(getNetworkFromChainId(value));
  }, [supportedChains]);

  const filterOptions = (
    value: string,
    item: { value: string; label: string }
  ) =>
    item !== undefined &&
    item?.label?.toLowerCase().includes(value.toLowerCase().trim());

  const form = useCreateCollectionFormContext();
  const formValues = form.values;

  return (
    <Stack spacing={spacing}>
      <Stack spacing={8}>
        <Text component="label" size="sm">
          Network
        </Text>
        <Grid>
          <Grid.Col span={6}>
            <SelectBox
              checked={!mainnetSelected}
              label="Testnet"
              description="Use a testnet for testing your collection"
              onClick={() => {
                if (mainnetSelected) {
                  handleNetworkChange(
                    l2TestnetSelected
                      ? NetworkType.BASE_SEPOLIA
                      : NetworkType.SEPOLIA
                  );
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <SelectBox
              checked={mainnetSelected}
              label="Mainnet"
              description="Use a mainnet for the final product"
              onClick={() => {
                if (!mainnetSelected) {
                  handleNetworkChange(mainnetNetwork);
                }
              }}
            />
          </Grid.Col>
        </Grid>
      </Stack>
      {mainnetSelected ? (
        <Select
          label="Blockchain"
          description={`This collection will be created using the ${
            formValues.contract.data.contractStandard ===
            NftContractStandard.ERC1155
              ? "ERC-1155"
              : "ERC-721"
          } standard`}
          placeholder={placeholder}
          itemComponent={SelectItem}
          data={mainnetOptions}
          searchable={searchable}
          maxDropdownHeight={400}
          nothingFound="Chain not found"
          filter={filterOptions}
          icon={getNativeCurrencyIcon(mainnetNetwork, 20)}
          value={mainnetNetwork}
          onChange={handleNetworkChange}
          {...props}
        />
      ) : (
        <Stack spacing={8}>
          <Stack spacing={1}>
            <Text component="label" size={14}>
              Blockchain
            </Text>
            <Text size={14.4} weight={400}>
              This collection will be created using the{" "}
              {formValues.contract.data.contractStandard ===
              NftContractStandard.ERC1155
                ? "ERC-1155"
                : "ERC-721"}{" "}
              standard
            </Text>
          </Stack>
          <Grid>
            <Grid.Col span={6}>
              <SelectBox
                checked={!l2TestnetSelected}
                label="Ethereum Sepolia"
                onClick={() => {
                  handleNetworkChange(NetworkType.SEPOLIA);
                }}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <SelectBox
                checked={l2TestnetSelected}
                label="Base Sepolia"
                onClick={() => {
                  handleNetworkChange(NetworkType.BASE_SEPOLIA);
                }}
              />
            </Grid.Col>
          </Grid>
          <Text size="xs">
            Need testnet ETH for{" "}
            {l2TestnetSelected ? "Base Sepolia" : "Sepolia"}? Get it for free
            from a faucet.&nbsp;
            <Anchor
              size="xs"
              weight={500}
              color={TEXT_COLOR.INVERTED_PRIMARY}
              href={EXTERNAL_URL.FAUCET_HELP_ARTICLE}
              target="_blank"
            >
              Learn more
            </Anchor>
          </Text>
        </Stack>
      )}
    </Stack>
  );
};

export default memo(BlockchainSelect);
